/**
 * 引き出しの「声の聞き比べ」（ADR-0064）。同じ文を 2 つの声で読んだ音を A / B で鳴らし、「A がよい / 同じ / B がよい」を選ぶ。
 * 待ちが無ければ何も出さない（呼ぶ側が枠ごと隠す）。勝手には鳴らさない（押したときだけ）。
 * iPhone: 押した操作の中で <audio> を鳴らせる状態にする（BrowserOutput.unlock、読み上げと同じやり方）。音は描くときに先に取っておき、
 * 押したらすぐ鳴らす（取れていなければ、unlock してから取って鳴らす）。
 * 声の端末・開発用トークン・未適用の環境では出ない（Gateway が 403・空を返す）。
 */
import { ApiError, api, GATEWAY, store } from "./api.js";
import type { BrowserOutput } from "./speech.js";
import {
  VOICE_AB_CHOICES,
  VOICE_AB_COMMENT_MAX,
  type VoiceAbView,
  voiceAbAnswerBody,
  voiceAbCardModel,
  voiceAbErrorText,
} from "./voice-ab-model.js";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: (Node | string)[]
) {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  // 文字列は Text ノードとして入れる（文を HTML として解釈しない）
  e.append(...children);
  return e;
}

async function fetchAudio(trial_id: string, side: "a" | "b", signal: AbortSignal): Promise<Blob> {
  const res = await fetch(
    `${GATEWAY}/v1/voice-ab/audio?trial_id=${encodeURIComponent(trial_id)}&side=${side}`,
    { headers: { Authorization: `Bearer ${store.token ?? ""}` }, signal },
  );
  if (!res.ok) {
    const j = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(res.status, j.error ?? "error");
  }
  return await res.blob();
}

/** 描き直すたびに前の取り込み・鳴らしを止める */
const live = new WeakMap<HTMLElement, AbortController>();

export async function renderVoiceAbCard(
  root: HTMLElement,
  opts: {
    output: BrowserOutput;
    view?: VoiceAbView;
    /** 描き終えたとき（答えた後の描き直しも）。呼ぶ側が空なら枠ごと隠す */
    onRendered?: () => void;
  },
): Promise<void> {
  live.get(root)?.abort();
  const ac = new AbortController();
  live.set(root, ac);
  let view: VoiceAbView | null = opts.view ?? null;
  if (!view) {
    try {
      view = await api<VoiceAbView>("/v1/voice-ab/next");
    } catch (e) {
      if (!(e instanceof ApiError)) console.warn(e);
      root.replaceChildren();
      opts.onRendered?.();
      return;
    }
  }
  if (ac.signal.aborted) return;
  const m = voiceAbCardModel(view);
  const trial = view.trial;
  if (!m.show || !trial) {
    root.replaceChildren();
    opts.onRendered?.();
    return;
  }

  const status = el("p", { class: "small muted", role: "status" });
  const plays = { a: 0, b: 0 };
  // 音は先に取っておく（鳴らさない）。押したときに操作の中で鳴らせるように
  const audio = {
    a: fetchAudio(trial.trial_id, "a", ac.signal),
    b: fetchAudio(trial.trial_id, "b", ac.signal),
  };
  const ready: { a: Blob | null; b: Blob | null } = { a: null, b: null };
  for (const side of ["a", "b"] as const)
    audio[side].then(
      (b) => {
        ready[side] = b;
      },
      () => undefined,
    );
  let playing: AbortController | null = null;
  const playBtn = (side: "a" | "b") => {
    const b = el(
      "button",
      { type: "button", class: "voice-ab-play", "aria-label": `${side.toUpperCase()} を鳴らす` },
      el("span", { class: "jv-latin" }, side.toUpperCase()),
      " を聞く",
    ) as HTMLButtonElement;
    b.onclick = async () => {
      playing?.abort();
      const p = new AbortController();
      playing = p;
      ac.signal.addEventListener("abort", () => p.abort(), { once: true });
      // iPhone: 取れていなければ、押した操作の中（await の前）で鳴らせる状態にしておく
      const blob = ready[side];
      if (!blob) opts.output.unlock();
      try {
        b.classList.add("playing");
        status.textContent = "";
        plays[side] += 1;
        await opts.output.playAudio(blob ?? (await audio[side]), p.signal);
      } catch (e) {
        if (p.signal.aborted) return;
        status.textContent =
          e instanceof ApiError
            ? voiceAbErrorText(e.code)
            : "鳴らせませんでした。もう一度押してください。";
      } finally {
        b.classList.remove("playing");
      }
    };
    return b;
  };
  const comment = el("textarea", {
    rows: "2",
    maxlength: String(VOICE_AB_COMMENT_MAX),
    placeholder: "一言（任意）: どこがよかったか・気になったか",
    "aria-label": "一言（任意）",
  }) as HTMLTextAreaElement;
  const choiceBtns = VOICE_AB_CHOICES.map(({ choice, label }) => {
    const b = el(
      "button",
      { type: "button", class: `voice-ab-choice ${choice}` },
      label,
    ) as HTMLButtonElement;
    b.onclick = async () => {
      for (const x of choiceBtns) x.disabled = true;
      playing?.abort();
      try {
        const next = await api<VoiceAbView>(
          "/v1/voice-ab/answer",
          voiceAbAnswerBody(trial.trial_id, choice, comment.value, plays),
        );
        await renderVoiceAbCard(root, { ...opts, view: next });
      } catch (e) {
        status.textContent = voiceAbErrorText(e instanceof ApiError ? e.code : String(e));
        for (const x of choiceBtns) x.disabled = false;
      }
    };
    return b;
  });

  root.replaceChildren(
    el(
      "section",
      { class: "card voice-ab" },
      el("p", { class: "voice-ab-sentence" }, m.sentence),
      el("div", { class: "row voice-ab-plays" }, playBtn("a"), playBtn("b")),
      el("div", { class: "row voice-ab-choices" }, ...choiceBtns),
      comment,
      el("p", { class: "small muted" }, `${m.remaining}。どちらがどの声かは出しません。`),
      status,
    ),
  );
  opts.onRendered?.();
}
